// Menu structure only. All dish data (names, prices, descriptions, images, tags)
// comes live from the HappySpace Restaurant SDK (useMenu()) via src/lib/useMenuData.ts —
// nothing below is a hardcoded dish. This file defines only the section shell: which
// category ids exist, the display title (must match the SDK category name so live
// items get grouped into it), and the editorial kicker/blurb copy for that category
// on the Menu page. If the SDK has no items for a category yet, that section simply
// renders empty — never fabricated placeholder dishes.

export type Tag = 'veg' | 'egg' | 'nonveg';

export interface MenuItem {
  name: string;
  price: number;
  tag: Tag;
  signature?: boolean;
  desc?: string;
  weekend?: boolean;
  disabled?: boolean;
  /** Raw SDK menu item, passed to addItem(). */
  raw?: any;
  /** Present when a promo deal applies — pre-discount price, shown struck through. */
  originalPrice?: number;
  /** Present when a promo deal applies — the % off, e.g. 25. */
  dealPercent?: number;
  /** Live SDK photo URL, when the item has one uploaded. */
  img?: string;
}

export interface MenuSection {
  id: string;
  title: string;
  kicker: string;
  blurb?: string;
  items: MenuItem[];
}

export const MENU: MenuSection[] = [
  { id: 'starters-veg', title: 'Vegetarian Starters', kicker: 'To Begin', blurb: 'Crisp, wok-tossed small plates, the way South Indian street corners do it.', items: [] },
  { id: 'starters-nonveg', title: 'Non-Veg Starters', kicker: 'To Begin', blurb: 'Dry fries and varuvals built on Chettinad and Kongunad spice traditions.', items: [] },
  { id: 'soups', title: 'Soups', kicker: 'Warm Up', items: [] },
  { id: 'snacks', title: 'Snacks', kicker: 'Petti Kadai', blurb: 'Old-school tuck-shop bites, perfect with a cup of chai.', items: [] },
  { id: 'tiffin', title: 'Idli & Tiffin', kicker: 'Steamed Fresh', items: [] },
  { id: 'dosa', title: 'Dosa', kicker: 'Griddle', blurb: 'Fermented rice-and-lentil crêpes crisped to lace on the tawa.', items: [] },
  { id: 'aapam', title: 'Aapam & Idiyappam', kicker: 'Coconut Country', blurb: 'Lacy hoppers and string hoppers from the Kerala-Tamil border, with salna.', items: [] },
  { id: 'parotta', title: 'Parotta', kicker: 'The House Specialty', blurb: 'Our namesake, layered, hand-slapped flatbread folded fresh and paired with salna.', items: [] },
  { id: 'special-parotta', title: 'Special Parottas', kicker: 'Signature', blurb: 'Stuffed, chilli-tossed and pot-steamed parottas you will not find everywhere.', items: [] },
  { id: 'kothu', title: 'Kothu Parotta', kicker: 'Chopped & Sizzled', blurb: 'Parotta minced on the griddle with egg, masala and your choice of protein.', items: [] },
  { id: 'curries-veg', title: 'Vegetarian Curries', kicker: 'Gravies', blurb: 'Slow-cooked gravies to scoop up with parotta, dosa or rice.', items: [] },
  { id: 'curries-nonveg', title: 'Non-Veg Curries', kicker: 'Gravies', blurb: 'Egg, chicken, mutton, shrimp and fish gravies across regional styles.', items: [] },
  { id: 'biryani', title: 'Dum Biryani', kicker: 'Sealed & Slow-Cooked', blurb: 'Seeraga samba rice layered with spice and sealed to finish on dum.', items: [] },
  { id: 'fried-rice', title: 'Fried Rice', kicker: 'Wok', items: [] },
  { id: 'pulav', title: 'Pulav', kicker: 'Fragrant Rice', items: [] },
  { id: 'buffet', title: 'Buffet', kicker: 'All You Can Eat', items: [] },
  { id: 'meals', title: 'Meals & Thali', kicker: 'The Full Plate', blurb: 'A complete South Indian meal, rice, curries, sides and accompaniments.', items: [] },
  { id: 'sides', title: 'Sides & Add-Ons', kicker: 'A Little Extra', items: [] },
  { id: 'beverages', title: 'Beverages', kicker: 'To Sip', blurb: 'Boiling-hot filter coffee, Irani chai and nostalgic coolers.', items: [] },
  { id: 'desserts', title: 'Desserts', kicker: 'Sweet Finish', items: [] },
];

export const SIGNATURES: MenuItem[] = MENU.flatMap((s) => s.items).filter((i) => i.signature);

export const ITEM_COUNT = MENU.reduce((n, s) => n + s.items.length, 0);
